/* eslint-disable max-len */
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import { FormBuilder } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { selectCurrentDeviceInfo, selectMtmaChanges } from 'src/app/store/device/device.selector';
import { filter, Observable, Subscription } from 'rxjs';
import { DeviceInfo } from 'src/app/model/device-info';
import { StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { ConfirmationService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { User } from 'src/app/model/user';
import { selectCurrentLang, selectIsDraftMode, selectIsImpersMode, selectUser } from 'src/app/store/user/user.selector';
import { Lang } from 'src/app/model/lang';
import { DEFAULT_LANG } from 'src/app/conf/languages';
import { AppComponent } from 'src/app/app.component';
import * as UserFileActions from 'src/app/store/user-file/user-file.actions';
import { MtmaExportDeviceDialogComponent } from '../mtma-export-dialog/mtma-export-dialog.component';
import { MtmaImportFileDialogComponent } from '../mtma-import-file-dialog/mtma-import-file.dialog.component';
import { ImportFileSharingSuccess } from 'src/app/conf/devices-vct';

@Component({
	selector: 'app-mtma-import-export-tab',
	templateUrl: './mtma-import-export-tab.component.html',
	providers: [ConfirmationService, DialogService]
})

export class MtmaImportExportComponent implements OnInit, OnDestroy {
	deviceInfo: DeviceInfo;
	deviceInfo$: Observable<DeviceInfo | undefined>;
	deviceInfoSub: Subscription | undefined;

	currentUser: User;
	currentUser$: Observable<User | undefined>;
	currentUserSub: Subscription | undefined;

	isImpersMode: boolean;
	isImpersMode$: Observable<boolean | undefined>;
	isImpersModeSub: Subscription | undefined;

	isDraftMode: boolean;
	isDraftMode$: Observable<boolean | undefined>;
	isDraftModeSub: Subscription;

	mtmaChanges: boolean;
	mtmaChanges$: Observable<boolean | undefined>;
	mtmaChangesSub: Subscription | undefined;

	currentLang$: Observable<Lang> = new Observable();
	currentLang: Lang = DEFAULT_LANG;

	ref: DynamicDialogRef | undefined;

	ImportFileSharingSuccess = ImportFileSharingSuccess;
	importFileSuccess = ImportFileSharingSuccess.NOT_UPLOADED; // -1: not imported due to block errors, 0: not uploaded, 1: imported

	screenWidth: number;
	acceptButtonStyle: string;
	rejectButtonStyle: string;

	constructor(
		private store: Store<AppState>,
		private formBuilder: FormBuilder,
		private translate: TranslateService,
		public confirmationService: ConfirmationService,
		public app: AppComponent,
		public dialogService: DialogService
	) {
		this.initSelectors();
	}

	initSelectors(): void {
		this.deviceInfo$ = this.store.select(selectCurrentDeviceInfo);
		this.currentUser$ = this.store.select(selectUser);
		this.currentLang$ = this.store.select(selectCurrentLang);
		this.isImpersMode$ = this.store.select(selectIsImpersMode);
		this.isDraftMode$ = this.store.select(selectIsDraftMode);
		this.mtmaChanges$ = this.store.select(selectMtmaChanges);
	}

	ngOnInit(): void {
		this.subscribeAll();

		this.onResize();

	}

	subscribeAll(): void {
		this.deviceInfoSub = this.deviceInfo$.pipe(filter(d => !!d))
			.subscribe(currentDeviceInfo => {
				this.deviceInfo = currentDeviceInfo;
			});
		this.currentUserSub = this.currentUser$?.subscribe(currentUser => {
			this.currentUser = currentUser;
		});
		this.currentLang$?.subscribe((lang: Lang) => {
			this.currentLang = lang;
		});
		this.isImpersModeSub = this.isImpersMode$?.subscribe(
			(isImpersMode) => {
				this.isImpersMode = isImpersMode;
			}
		);
		this.isDraftModeSub = this.isDraftMode$.subscribe(
			(isDraftMode) => {
				this.isDraftMode = isDraftMode;
			}
		);
		this.mtmaChangesSub = this.mtmaChanges$.subscribe(mtmaChanges => {
			this.mtmaChanges = mtmaChanges;
		});
	}

	ngOnDestroy(): void {
		this.deviceInfoSub?.unsubscribe();
		this.currentUserSub?.unsubscribe();
		this.isImpersModeSub?.unsubscribe();
		this.isDraftModeSub?.unsubscribe();
		this.mtmaChangesSub?.unsubscribe();
	}

	compareVersions(ver1: string, ver2: string): boolean {
		// prevent missing versions v1 and/or v2
		if (!ver1) return false;
		if (!ver2) return true;

		const a = ver1.split('.').map(Number);
		const b = ver2.split('.').map(Number);

		const maxLength = Math.max(a.length, b.length);

		for (let i = 0; i < maxLength; i++) {
			const num1 = a[i] || 0; // if an element is missing, use 0 (default)
			const num2 = b[i] || 0;

			if (num1 > num2) return true;
			if (num1 < num2) return false;
		}

		return true; // if all parts are equal
	}

	exportData(): void {
		this.store.dispatch(UserFileActions.resetMtmaExportDeviceDialog());
		this.ref = this.dialogService.open(MtmaExportDeviceDialogComponent, {
			header: this.translate.instant('EXPORT_DATA_FROM_DEVICE'),
			width: '70vw',
			breakpoints: { '1120px': '98vw' },
			contentStyle: { overflow: 'auto' },
		});
	}

	importFromFile(): void {
		this.ref = this.dialogService.open(MtmaImportFileDialogComponent, {
			header: this.translate.instant('IMPORT_DATAS_FROM_FILE'),
			width: '80vw',
			breakpoints: { '1220px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {}
		});
		this.ref.onClose.subscribe((importFileSuccess) => {
			this.importFileSuccess = importFileSuccess;
		});
	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	col6ResponsiveButtonLayout(position: string): string {
		return StandardPageLayout.col6ResponsiveButtonLayout(this.screenWidth, position);
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}
}
